// src/pages/ProjectInputPage.jsx

import React, { useState, useEffect } from "react";
import {
  TrashIcon,
  PlusIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon,
  CalculatorIcon,
} from "@heroicons/react/outline";
import { Dialog } from "@headlessui/react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../../api";

// Guide Modal
function GuideModal({ isOpen, onClose, title, content }) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black opacity-30" />
      <Dialog.Panel className="bg-white rounded p-6 z-10 max-w-md w-full">
        <Dialog.Title className="text-xl font-bold mb-2">
          {title} Guide
        </Dialog.Title>
        <Dialog.Description className="mb-4 whitespace-pre-line">
          {content}
        </Dialog.Description>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded"
        >
          Close
        </button>
      </Dialog.Panel>
    </Dialog>
  );
}

// Dynamic Table
function DynamicTable({
  sectionKey,
  rows = [],
  setRows,
  columns,
  rowDefaults = {},
}) {
  const deleteRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateField = (i, field, value) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  return (
    <div className="w-full overflow-x-auto mb-4">
      <table className="w-full table-auto border border-gray-200 rounded">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((col) => (
              <th
                key={col.field}
                className={`px-2 py-1 text-center text-sm font-medium text-gray-700 border-b border-gray-200 ${
                  col.field.toLowerCase().includes("slno") ? "w-12" : ""
                }`}
              >
                {col.header}
              </th>
            ))}
            <th className="px-2 py-1 text-center text-sm font-medium text-gray-700 border-b border-gray-200 w-12">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 ? "bg-gray-50" : ""}>
              {columns.map((col) => {
                const id = `${sectionKey}-${i}-${col.field}`;
                let opts = [];
                if (col.type === "select") {
                  opts =
                    typeof col.options === "function"
                      ? col.options(row)
                      : col.options;
                }
                return (
                  <td
                    key={col.field}
                    className={`px-2 py-1 text-sm border-b border-gray-200 ${
                      col.field.toLowerCase().includes("slno") ? "w-12" : ""
                    }`}
                  >
                    {col.type === "select" ? (
                      <select
                        id={id}
                        className="w-full text-sm p-1 border border-gray-300 rounded text-center"
                        value={row[col.field] || ""}
                        onChange={(e) =>
                          updateField(i, col.field, e.target.value)
                        }
                      >
                        <option value="">None</option>
                        {(opts || []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={id}
                        type={col.type || "text"}
                        className="w-full text-sm p-1 border border-gray-300 rounded text-center"
                        value={row[col.field] || ""}
                        onChange={(e) =>
                          updateField(i, col.field, e.target.value)
                        }
                      />
                    )}
                  </td>
                );
              })}

              <td className="px-2 py-1 text-center border-b border-gray-200 w-12">
                <button
                  onClick={() => deleteRow(i)}
                  className="inline-flex items-center justify-center h-6 w-6 bg-red-100 hover:bg-red-200 rounded-full no-print"
                >
                  <TrashIcon className="h-4 w-4 text-red-600" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProjectInputPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const project = location.state?.project;

  // ✅ Only guard against "no project found" (do NOT check isPaid here)
  useEffect(() => {
    if (!project) {
      navigate("/user/dashboard", { replace: true });
    }
  }, [project, navigate]);

  // Guide modal state
  const [guide, setGuide] = useState({ open: false, title: "", content: "" });
  const showGuide = (t, c) => setGuide({ open: true, title: t, content: c });
  const closeGuide = () => setGuide((g) => ({ ...g, open: false }));

  // 🔹 Guidelines from backend (sectionName -> guide)
  const [guidesMap, setGuidesMap] = useState({});
  const [guidesStatus, setGuidesStatus] = useState("loading"); // loading | success | error

  useEffect(() => {
    let isMounted = true;
    API.get("/guidelines")
      .then(({ data }) => {
        if (!isMounted) return;
        const map = {};
        (data || []).forEach((g) => {
          if (g.sectionName) {
            map[g.sectionName] = g.guide || "";
          }
        });
        setGuidesMap(map);
        setGuidesStatus("success");
      })
      .catch((err) => {
        console.error("Failed to load guidelines:", err);
        if (!isMounted) return;
        setGuidesStatus("error");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const getGuideContent = (sectionName) => {
    if (guidesStatus !== "success") return "Error Loading Guidelines";
    const txt = guidesMap[sectionName];
    if (!txt || !String(txt).trim()) return "Error Loading Guidelines";
    return txt;
  };

  // STATE HOOKS for all inputs
  const [projectYears, setProjectYears] = useState([]);
  const [capUtil, setCapUtil] = useState([]);
  const [productRows, setProductRows] = useState([]);
  const [plantRows, setPlantRows] = useState([]);
  const [plantCont, setPlantCont] = useState(0);
  const [plantGst, setPlantGst] = useState(0);
  const [furnitureRows, setFurnitureRows] = useState([]);
  const [otherFixedRows, setOtherFixedRows] = useState([]);
  const [computerRows, setComputerRows] = useState([]);
  const [prelimRows, setPrelimRows] = useState([]);
  const [consumablesExpensePct, setConsumablesExpensePct] = useState("");
  const [manualRows, setManualRows] = useState([]);
  const [labourRows, setLabourRows] = useState([]);
  const [extrasPf, setExtrasPf] = useState(0);
  const [deprRows, setDeprRows] = useState([]);
  const [costInputs, setCostInputs] = useState({
    landDevelopment: "",
    civilConstruction: "",
    deposits: "",
    termLoanPct: "",
    equityPct: "",
    workingCapitalPct: "",
  });
  const [rmRows, setRmRows] = useState([]);
  const [rmPartRows, setRmPartRows] = useState([]);
  const [costProdRows, setCostProdRows] = useState([]);
  const [powerRows, setPowerRows] = useState([]);

  const powerOptions = [
    "Total Connected Power Load (KW)",
    "Power Factor",
    "Average Rate per Unit",
    "Fixed Cost (Per Month)",
  ];

  const [salesRows, setSalesRows] = useState([]);
  const [wcRows, setWcRows] = useState([]);
  const [workingCapitalInputs, setWorkingCapitalInputs] = useState({
    bankContribution: "",
    interestOnCC: "",
  });
  const [projProfit, setProjProfit] = useState({
    leaseRent: "",
    preliminaryExpensesWrittenOff: "",
    sellingAndDistributionExpense: "",
    incrementExpensePerYear: "",
    provisionForTax: "",
  });
  const [breakEven, setBreakEven] = useState({
    sellingExpense: "",
    fixedSellingExpense: "",
  });
  const [balanceSheet, setBalanceSheet] = useState({
    depositsB: "",
    loanAndAdvances: "",
  });
  const [otherCurrentAssetsByYear, setOtherCurrentAssetsByYear] = useState([]);
  const [projCashFlow, setProjCashFlow] = useState([]);
  const [promoterDrawings, setPromoterDrawings] = useState([]);
  const [sens1, setSens1] = useState("");
  const [sens2, setSens2] = useState("");
  const [termLoan, setTermLoan] = useState({
    interestRate: "",
    loanPeriod: "",
    moratorium: "",
    startMonth: "",
    pStartMonth: "",
    disbursements: [""],
  });

  const updateDisb = (i, v) => {
    const d = [...termLoan.disbursements];
    d[i] = v;
    setTermLoan({ ...termLoan, disbursements: d });
  };
  const addDisb = () =>
    setTermLoan({
      ...termLoan,
      disbursements: [...termLoan.disbursements, ""],
    });
  const removeDisb = (i) =>
    setTermLoan({
      ...termLoan,
      disbursements: termLoan.disbursements.filter((_, idx) => idx !== i),
    });

  // flag to know when API load is done (success or 404)
  const [hasLoadedInputs, setHasLoadedInputs] = useState(false);

  // Section open/close state
  const sectionKeys = [
    "projectYears",
    "capacityUtilization",
    "productManufactured",
    "plantMachinery",
    "furniture",
    "otherFixedAssets",
    "computers",
    "preliminaryExpenses",
    "consumablesExpenseDetails",
    "labourCost",
    "depreciation",
    "costOfProject",
    "rmConsumption",
    "costOfProduction",
    "powerSection",
    "annualSales",
    "workingCapitalRequirement",
    "projectedProfitability",
    "breakEven",
    "balanceSheet",
    "projectedCashFlow",
    "sensitivityAnalysis",
    "termLoanRepayment",
  ];

  const defaultClosed = sectionKeys.reduce(
    (acc, k) => ((acc[k] = false), acc),
    {}
  );

  const [openSections, setOpenSections] = useState(defaultClosed);

  const toggle = (k) => setOpenSections((os) => ({ ...os, [k]: !os[k] }));

  const next = (k) => {
    const i = sectionKeys.indexOf(k);
    if (i >= 0 && i < sectionKeys.length - 1) {
      const nk = sectionKeys[i + 1];
      setOpenSections((os) => ({ ...os, [k]: false, [nk]: true }));
      document.getElementById(nk)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Load existing data
  useEffect(() => {
    if (!projectId) {
      setHasLoadedInputs(true);
      return;
    }

    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setProjectYears(data.projectYears || []);
        setCapUtil(data.capacityUtilizationPerYear || []);
        setProductRows(data.manufacturedProducts || []);
        setPlantRows(data.plantMachineryData || []);
        setPlantCont(data.plantContingencies || 0);
        setPlantGst(data.plantGstPercent || 0);
        setFurnitureRows(data.furnitureAndFixtures || []);
        setOtherFixedRows(data.otherFixedAssets || []);
        setComputerRows(data.computers || []);
        setPrelimRows(data.preliminaryExpenses || []);
        setConsumablesExpensePct(data.consumablesExpensePct || "");
        setManualRows(data.manualConsumables || []);
        setLabourRows(data.labourCost || []);
        setExtrasPf(data.labourExtraPercent || 0);
        setDeprRows(data.depreciationData || []);
        setCostInputs(data.costProjectInputs || {});
        setRmRows(data.rmConsumptionInputs || []);
        setRmPartRows(data.rmConsumptionParticulars || []);
        setCostProdRows(data.costOfProductionData || []);
        setPowerRows(data.powerSectionData || []);
        setSalesRows(data.annualSalesRealisationInputs || []);
        setWcRows(data.workingCapitalRequirement || []);
        setWorkingCapitalInputs(data.workingCapitalInputs || {});
        setProjProfit(data.projectedProfitabilityInputs || {});
        setBreakEven(data.breakEvenAnalysisInputs || {});
        setBalanceSheet(data.balanceSheetInputs || {});
        setOtherCurrentAssetsByYear(data.otherCurrentAssetsByYear || []);
        setProjCashFlow(data.projectedCashFlowFixedAssets || []);
        setPromoterDrawings(data.promoterDrawings || []);
        setSens1(data.sensitivityScenario1 || "");
        setSens2(data.sensitivityScenario2 || "");

        setTermLoan((prev) => {
          const incoming = data.termLoanInput || {};

          const merged = {
            interestRate: "",
            loanPeriod: "",
            moratorium: "",
            startMonth: "",
            pStartMonth: "",
            disbursements: [""],
            ...prev,
            ...incoming,
          };

          if (data.startMonth && !merged.startMonth) {
            merged.startMonth = data.startMonth;
          }
          if (data.pStartMonth && !merged.pStartMonth) {
            merged.pStartMonth = data.pStartMonth;
          }

          if (
            !Array.isArray(merged.disbursements) ||
            merged.disbursements.length === 0
          ) {
            merged.disbursements = [""];
          }

          return merged;
        });

        setHasLoadedInputs(true);
      })
      .catch((err) => {
        if (err?.response?.status !== 404) console.error(err);
        setHasLoadedInputs(true);
      });
  }, [projectId]);

  // After data load: auto-generate Project Years & Capacity Utilization defaults if empty
  useEffect(() => {
    if (!hasLoadedInputs) return;

    if (!projectYears || projectYears.length === 0) {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 1;
      const fy = [];
      for (let i = 0; i < 8; i++) {
        const y1 = startYear + i;
        const y2 = (y1 + 1).toString().slice(-2);
        fy.push(`${y1}-${y2}`);
      }
      setProjectYears(fy);

      if (!capUtil || capUtil.length === 0) {
        const defaultCap = fy.map((_, idx) => 50 + idx * 5);
        setCapUtil(defaultCap);
      }
    } else if (!capUtil || capUtil.length === 0) {
      const defaultCap = projectYears.map((_, idx) => 50 + idx * 5);
      setCapUtil(defaultCap);
    }
  }, [hasLoadedInputs, projectYears, capUtil]);

  // Build payload (shared by Save & Save & Submit)
  const buildPayload = () => ({
    projectYears,
    capacityUtilizationPerYear: capUtil,
    manufacturedProducts: productRows,
    plantMachineryData: plantRows,
    plantContingencies: plantCont,
    plantGstPercent: plantGst,
    furnitureAndFixtures: furnitureRows,
    otherFixedAssets: otherFixedRows,
    computers: computerRows,
    preliminaryExpenses: prelimRows,
    depreciationData: deprRows,
    costProjectInputs: costInputs,
    consumablesExpensePct,
    manualConsumables: manualRows,
    labourCost: labourRows,
    labourExtraPercent: extrasPf,
    rmConsumptionInputs: rmRows,
    rmConsumptionParticulars: rmPartRows,
    costOfProductionData: costProdRows,
    powerSectionData: powerRows,
    annualSalesRealisationInputs: salesRows,
    workingCapitalRequirement: wcRows,
    workingCapitalInputs,
    projectedProfitabilityInputs: projProfit,
    breakEvenAnalysisInputs: breakEven,
    balanceSheetInputs: balanceSheet,
    otherCurrentAssetsByYear,
    projectedCashFlowFixedAssets: projCashFlow,
    promoterDrawings,
    sensitivityScenario1: sens1,
    sensitivityScenario2: sens2,
    startMonth: termLoan.startMonth ? String(termLoan.startMonth) : "",
    pStartMonth: termLoan.pStartMonth ? String(termLoan.pStartMonth) : "",
    termLoanInput: {
      ...termLoan,
      startMonth: termLoan.startMonth ? String(termLoan.startMonth) : "",
    },
  });

  const saveOnly = async () => {
    const payload = buildPayload();
    try {
      await API.put(`/user/project-inputs/${projectId}`, payload);
    } catch {
      alert("Save failed.");
    }
  };

  const saveData = async () => {
    const payload = buildPayload();
    try {
      await API.put(`/user/project-inputs/${projectId}`, payload);
      navigate(`/project-inputs/${projectId}/report`);
    } catch {
      alert("Save failed.");
    }
  };

  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    const allOpen = sectionKeys.reduce((acc, k) => ((acc[k] = true), acc), {});
    setOpenSections(allOpen);
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setOpenSections(defaultClosed);
      setIsPrinting(false);
    }, 300);
  };

  return (
    <div
      className={`space-y-2 print-container ${
        isPrinting ? " print-scale" : ""
      }`}
    >
      {/* watermark safe */}
      {project?.companyName && (
        <div className="print-watermark">{project.companyName}</div>
      )}

      <GuideModal
        isOpen={guide.open}
        onClose={closeGuide}
        title={guide.title}
        content={guide.content}
      />

      <div className="flex place-content-between">
        {/* Top Buttons */}
        <div className="flex justify-end space-x-2 no-print">
          <button
            onClick={handlePrint}
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          >
            Print PDF
          </button>
          <button
            onClick={saveOnly}
            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
          >
            Save
          </button>
          <button
            onClick={() => navigate(`/project-inputs/${projectId}/report`)}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Report
          </button>
          <button
            onClick={saveData}
            className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
          >
            Save &amp; Submit
          </button>
        </div>
        <div>
          <p className="bold">Rs In Lakhs</p>
        </div>
      </div>

      <div className="print-content space-y-2">
        <div className="w-full flex flex-col items-center justify-center text-center">
          <p className="font-bold text-2xl">{project?.companyName}</p>
          <p>{project?.companyAddress}</p>
          <p>
            {project?.email}, {project?.phone}
          </p>
          <p>{project?.idNumber}</p>
          <p>{project?.projectType}</p>
          <p>{project?.productNames}</p>
        </div>

        {/* 1) Project Years */}
        <section id="projectYears">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-medium">Project Years</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide("Project Years", getGuideContent("Project Years"))
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() => setProjectYears([...projectYears, ""])}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("projectYears")}
                className="p-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("projectYears")}
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                {openSections.projectYears ? (
                  <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.projectYears && (
            <div className="bg-white p-3 rounded shadow mt-1 flex flex-wrap gap-4">
              {projectYears.map((yr, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder={`Year ${i + 1}`}
                    className="w-24 p-2 border rounded text-center"
                    value={yr}
                    onChange={(e) => {
                      const a = [...projectYears];
                      a[i] = e.target.value;
                      setProjectYears(a);
                    }}
                  />
                  <button
                    onClick={() => {
                      const a = [...projectYears];
                      a.splice(i, 1);
                      setProjectYears(a);
                    }}
                    className="p-1 bg-red-200 rounded hover:bg-red-300"
                  >
                    <TrashIcon className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))}

              {/* Print-only guide */}
              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Project Years")}
              </div>
            </div>
          )}
        </section>

        {/* 2) Capacity Utilization */}
        <section id="capacityUtilization">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-base font-medium">Capacity Utilization</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Capacity Utilization",
                    getGuideContent("Capacity Utilization")
                  )
                }
                className="p-1 rounded-full bg-blue-100 hover:bg-blue-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-blue-600" />
              </button>
              <button
                onClick={() => setCapUtil([...capUtil, ""])}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("capacityUtilization")}
                className="p-1 rounded-full bg-blue-600 hover:bg-blue-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("capacityUtilization")}
                className="p-1 rounded-full bg-blue-100 hover:bg-blue-200"
              >
                {openSections.capacityUtilization ? (
                  <ChevronUpIcon className="h-4 w-4 text-blue-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.capacityUtilization && (
            <div className="bg-white p-3 rounded shadow mt-1 flex flex-wrap gap-4">
              {capUtil.map((val, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input
                    type="number"
                    placeholder={projectYears[i] || `Year ${i + 1}`}
                    className="w-24 p-2 border rounded text-center"
                    value={val}
                    onChange={(e) => {
                      const a = [...capUtil];
                      a[i] = e.target.value;
                      setCapUtil(a);
                    }}
                  />
                  <button
                    onClick={() => {
                      const a = [...capUtil];
                      a.splice(i, 1);
                      setCapUtil(a);
                    }}
                    className="p-1 bg-red-200 rounded hover:bg-red-300"
                  >
                    <TrashIcon className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))}

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Capacity Utilization")}
              </div>
            </div>
          )}
        </section>

        {/* 3) Product Manufactured */}
        <section id="productManufactured">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-base font-medium">Product Manufactured</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Product Manufactured",
                    getGuideContent("Product Manufactured")
                  )
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() =>
                  setProductRows([...productRows, { product: "" }])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("productManufactured")}
                className="p-1 rounded-full bg-green-600 hover:bg-green-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("productManufactured")}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                {openSections.productManufactured ? (
                  <ChevronUpIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-green-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.productManufactured && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="productManufactured"
                rows={productRows}
                setRows={setProductRows}
                columns={[{ field: "product", header: "Product", width: 300 }]}
                rowDefaults={{ product: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Product Manufactured")}
              </div>
            </div>
          )}
        </section>

        {/* 4) Plant & Machinery */}
        <section id="plantMachinery">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-pink-600" />
              <h2 className="text-base font-medium">Plant & Machinery</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Plant & Machinery",
                    getGuideContent("Plant & Machinery")
                  )
                }
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-pink-600" />
              </button>
              <button
                onClick={() =>
                  setPlantRows([
                    ...plantRows,
                    {
                      slNo: "",
                      particular: "",
                      supplier: "",
                      quantity: "",
                      amount: "",
                    },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("plantMachinery")}
                className="p-1 rounded-full bg-pink-600 hover:bg-pink-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("plantMachinery")}
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                {openSections.plantMachinery ? (
                  <ChevronUpIcon className="h-4 w-4 text-pink-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-pink-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.plantMachinery && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <DynamicTable
                sectionKey="plantMachinery"
                rows={plantRows}
                setRows={setPlantRows}
                columns={[
                  { field: "slNo", header: "Sl No", width: 60 },
                  { field: "particular", header: "Particular", width: 200 },
                  { field: "supplier", header: "Supplier", width: 150 },
                  {
                    field: "quantity",
                    header: "Quantity",
                    type: "number",
                    width: 80,
                  },
                  {
                    field: "amount",
                    header: "Amount",
                    type: "number",
                    width: 100,
                  },
                ]}
                rowDefaults={{
                  slNo: "",
                  particular: "",
                  supplier: "",
                  quantity: "",
                  amount: "",
                }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700">
                    Contingencies (%)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={plantCont}
                    onChange={(e) => setPlantCont(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">GST (%)</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={plantGst}
                    onChange={(e) => setPlantGst(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Plant & Machinery")}
              </div>
            </div>
          )}
        </section>

        {/* 5) Furniture & Fixtures */}
        <section id="furniture">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-purple-600" />
              <h2 className="text-base font-medium">Furniture & Fixtures</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Furniture & Fixtures",
                    getGuideContent("Furniture & Fixtures")
                  )
                }
                className="p-1 rounded-full bg-purple-100 hover:bg-purple-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-purple-600" />
              </button>
              <button
                onClick={() =>
                  setFurnitureRows([
                    ...furnitureRows,
                    { slNo: "", particular: "", amount: "" },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("furniture")}
                className="p-1 rounded-full bg-purple-600 hover:bg-purple-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("furniture")}
                className="p-1 rounded-full bg-purple-100 hover:bg-purple-200"
              >
                {openSections.furniture ? (
                  <ChevronUpIcon className="h-4 w-4 text-purple-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-purple-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.furniture && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="furniture"
                rows={furnitureRows}
                setRows={setFurnitureRows}
                columns={[
                  { field: "slNo", header: "Sl No", width: 60 },
                  { field: "particular", header: "Particular", width: 300 },
                  {
                    field: "amount",
                    header: "Amount",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ slNo: "", particular: "", amount: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Furniture & Fixtures")}
              </div>
            </div>
          )}
        </section>

        {/* 6) Other Fixed Assets */}
        <section id="otherFixedAssets">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-red-600" />
              <h2 className="text-base font-medium">Other Fixed Assets</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Other Fixed Assets",
                    getGuideContent("Other Fixed Assets")
                  )
                }
                className="p-1 rounded-full bg-red-100 hover:bg-red-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-red-600" />
              </button>
              <button
                onClick={() =>
                  setOtherFixedRows([
                    ...otherFixedRows,
                    { slNo: "", particular: "", amount: "" },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("otherFixedAssets")}
                className="p-1 rounded-full bg-red-600 hover:bg-red-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("otherFixedAssets")}
                className="p-1 rounded-full bg-red-100 hover:bg-red-200"
              >
                {openSections.otherFixedAssets ? (
                  <ChevronUpIcon className="h-4 w-4 text-red-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-red-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.otherFixedAssets && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="otherFixedAssets"
                rows={otherFixedRows}
                setRows={setOtherFixedRows}
                columns={[
                  { field: "slNo", header: "Sl No", width: 60 },
                  { field: "particular", header: "Particular", width: 300 },
                  {
                    field: "amount",
                    header: "Amount",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ slNo: "", particular: "", amount: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Other Fixed Assets")}
              </div>
            </div>
          )}
        </section>

        {/* 7) Computers & Software */}
        <section id="computers">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-teal-600" />
              <h2 className="text-base font-medium">Computers & Software</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Computers & Software",
                    getGuideContent("Computers & Software")
                  )
                }
                className="p-1 rounded-full bg-teal-100 hover:bg-teal-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-teal-600" />
              </button>
              <button
                onClick={() =>
                  setComputerRows([
                    ...computerRows,
                    { slNo: "", particular: "", amount: "" },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("computers")}
                className="p-1 rounded-full bg-teal-600 hover:bg-teal-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("computers")}
                className="p-1 rounded-full bg-teal-100 hover:bg-teal-200"
              >
                {openSections.computers ? (
                  <ChevronUpIcon className="h-4 w-4 text-teal-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-teal-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.computers && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="computers"
                rows={computerRows}
                setRows={setComputerRows}
                columns={[
                  { field: "slNo", header: "Sl No", width: 60 },
                  { field: "particular", header: "Particular", width: 300 },
                  {
                    field: "amount",
                    header: "Amount",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ slNo: "", particular: "", amount: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Computers & Software")}
              </div>
            </div>
          )}
        </section>

        {/* 8) Preliminary Expenses */}
        <section id="preliminaryExpenses">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-orange-600" />
              <h2 className="text-base font-medium">Preliminary Expenses</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Preliminary Expenses",
                    getGuideContent("Preliminary Expenses")
                  )
                }
                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-orange-600" />
              </button>
              <button
                onClick={() =>
                  setPrelimRows([
                    ...prelimRows,
                    { slNo: "", particular: "", amount: "" },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("preliminaryExpenses")}
                className="p-1 rounded-full bg-orange-600 hover:bg-orange-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("preliminaryExpenses")}
                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200"
              >
                {openSections.preliminaryExpenses ? (
                  <ChevronUpIcon className="h-4 w-4 text-orange-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-orange-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.preliminaryExpenses && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="preliminaryExpenses"
                rows={prelimRows}
                setRows={setPrelimRows}
                columns={[
                  { field: "slNo", header: "Sl No", width: 60 },
                  { field: "particular", header: "Particular", width: 300 },
                  {
                    field: "amount",
                    header: "Amount",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ slNo: "", particular: "", amount: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Preliminary Expenses")}
              </div>
            </div>
          )}
        </section>

        {/* 9) Consumables Expense Details */}
        <section id="consumablesExpenseDetails">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="h-5 w-5 text-cyan-600" />
              <h2 className="text-base font-medium">
                Consumables Expense Details
              </h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Consumables Expense Details",
                    getGuideContent("Consumables Expense Details")
                  )
                }
                className="p-1 rounded-full bg-cyan-100 hover:bg-cyan-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-cyan-600" />
              </button>
              <button
                onClick={() => next("consumablesExpenseDetails")}
                className="p-1 rounded-full bg-cyan-600 hover:bg-cyan-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("consumablesExpenseDetails")}
                className="p-1 rounded-full bg-cyan-100 hover:bg-cyan-200"
              >
                {openSections.consumablesExpenseDetails ? (
                  <ChevronUpIcon className="h-4 w-4 text-cyan-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-cyan-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.consumablesExpenseDetails && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700">
                    Particular
                  </label>
                  <input
                    type="text"
                    readOnly
                    className="w-full p-2 bg-gray-100 rounded text-center"
                    value="Percentage of expense on cartons, polybags, ropes etc"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">
                    Expense %
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={consumablesExpensePct}
                    onChange={(e) => setConsumablesExpensePct(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium">Manual Consumables</h3>
                  <div className="flex space-x-2 no-print">
                    <button
                      onClick={() =>
                        showGuide(
                          "Manual Consumables",
                          "List each manual consumable, its description and charge percentage to be applied on taxable amount."
                        )
                      }
                      className="p-1 rounded-full bg-cyan-100 hover:bg-cyan-200"
                    >
                      <InformationCircleIcon className="h-4 w-4 text-cyan-600" />
                    </button>
                    <button
                      onClick={() =>
                        setManualRows([
                          ...manualRows,
                          { slno: "", particular: "", taxPercent: "" },
                        ])
                      }
                      className="p-1 rounded-full bg-green-100 hover:bg-green-200"
                    >
                      <PlusIcon className="h-4 w-4 text-green-600" />
                    </button>
                  </div>
                </div>

                <DynamicTable
                  sectionKey="manualConsumables"
                  rows={manualRows}
                  setRows={setManualRows}
                  columns={[
                    { field: "slno", header: "Sl No", width: 60 },
                    { field: "particular", header: "Particular", width: 300 },
                    {
                      field: "taxPercent",
                      header: "Charge %",
                      type: "number",
                      width: 120,
                    },
                  ]}
                  rowDefaults={{ slno: "", particular: "", taxPercent: "" }}
                />
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Consumables Expense Details")}
              </div>
            </div>
          )}
        </section>

        {/* 10) Labour Cost Calculation */}
        <section id="labourCost">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-medium">Labour Cost Calculation</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Labour Cost Calculation",
                    getGuideContent("Labour Cost Calculation")
                  )
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() =>
                  setLabourRows([
                    ...labourRows,
                    { typeOfWork: "", manpower: "", nos: "", ratePerMonth: "" },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("labourCost")}
                className="p-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("labourCost")}
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                {openSections.labourCost ? (
                  <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.labourCost && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <DynamicTable
                sectionKey="labourCost"
                rows={labourRows}
                setRows={setLabourRows}
                columns={[
                  {
                    field: "typeOfWork",
                    header: "Type of Work",
                    type: "select",
                    options: ["Production", "Administration"],
                    width: 200,
                  },
                  {
                    field: "manpower",
                    header: "Manpower Req",
                    type: "select",
                    options: (row) =>
                      row.typeOfWork === "Administration"
                        ? ["Manager"]
                        : row.typeOfWork === "Production"
                        ? [
                            "Skilled Labour",
                            "Semi-skilled Labour",
                            "Unskilled Labour",
                          ]
                        : [],
                    width: 200,
                  },
                  { field: "nos", header: "Nos", type: "number", width: 100 },
                  {
                    field: "ratePerMonth",
                    header: "Rate / Month",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{
                  typeOfWork: "",
                  manpower: "",
                  nos: "",
                  ratePerMonth: "",
                }}
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium">Extras PF (%)</h3>
                  <button
                    onClick={() =>
                      showGuide(
                        "Extras PF (%)",
                        "Enter the percentage of extra PF and other statutory benefits to be added on total labour cost."
                      )
                    }
                    className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200 no-print"
                  >
                    <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
                  </button>
                </div>
                <input
                  type="number"
                  className="w-32 p-2 border rounded text-center"
                  value={extrasPf}
                  onChange={(e) => setExtrasPf(parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Labour Cost Calculation")}
              </div>
            </div>
          )}
        </section>

        {/* 11) Depreciation */}
        <section id="depreciation">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-indigo-500" />
              <h2 className="text-base font-medium">Depreciation</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide("Depreciation", getGuideContent("Depreciation"))
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() =>
                  setDeprRows([...deprRows, { particular: "", taxPercent: "" }])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("depreciation")}
                className="p-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("depreciation")}
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                {openSections.depreciation ? (
                  <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.depreciation && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="depreciation"
                rows={deprRows}
                setRows={setDeprRows}
                columns={[
                  {
                    field: "particular",
                    header: "Particular",
                    type: "select",
                    options: [
                      "Land & Site Development (On Lease)",
                      "Civil Construction (On Lease)",
                      "Plant & Machinery",
                      "Furniture and Fixture",
                      "Miscellaneous Fixed Assets",
                      "Computer and Software",
                    ],
                    width: 300,
                  },
                  {
                    field: "taxPercent",
                    header: "Charge %",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ particular: "", taxPercent: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Depreciation")}
              </div>
            </div>
          )}
        </section>

        {/* 12) Cost of the Project */}
        <section id="costOfProject">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CurrencyRupeeIcon className="h-5 w-5 text-red-600" />
              <h2 className="text-base font-medium">Cost of the Project</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Cost of the Project",
                    getGuideContent("Cost of the Project")
                  )
                }
                className="p-1 rounded-full bg-red-100 hover:bg-red-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-red-600" />
              </button>
              <button
                onClick={() => next("costOfProject")}
                className="p-1 rounded-full bg-red-600 hover:bg-red-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("costOfProject")}
                className="p-1 rounded-full bg-red-100 hover:bg-red-200"
              >
                {openSections.costOfProject ? (
                  <ChevronUpIcon className="h-4 w-4 text-red-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-red-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.costOfProject && (
            <div className="bg-white p-3 rounded shadow mt-1 grid grid-cols-1 md:grid-cols-6 gap-4 text-center">
              {[
                { field: "landDevelopment", label: "Land & Site Dev (%)" },
                { field: "civilConstruction", label: "Civil Const (%)" },
                { field: "deposits", label: "Deposits" },
                { field: "termLoanPct", label: "Term Loan (%)" },
                { field: "equityPct", label: "Equity (%)" },
                { field: "workingCapitalPct", label: "Working Cap (%)" },
              ].map(({ field, label }) => (
                <div key={field}>
                  <label className="block text-sm text-gray-700">{label}</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={costInputs[field] || ""}
                    onChange={(e) =>
                      setCostInputs({ ...costInputs, [field]: e.target.value })
                    }
                  />
                </div>
              ))}

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2 md:col-span-6">
                <strong>Guide:</strong> {getGuideContent("Cost of the Project")}
              </div>
            </div>
          )}
        </section>

        {/* 13) RM Consumption */}
        <section id="rmConsumption">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-base font-medium">RM Consumption</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide("RM Consumption", getGuideContent("RM Consumption"))
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() =>
                  setRmRows([
                    ...rmRows,
                    {
                      products: "",
                      workingDays: "",
                      workingHoursPerDay: "",
                      outputPerHour: "",
                      operationPeriod: "",
                    },
                  ])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("rmConsumption")}
                className="p-1 rounded-full bg-green-600 hover:bg-green-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("rmConsumption")}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                {openSections.rmConsumption ? (
                  <ChevronUpIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-green-600" />
                )}
              </button>
            </div>
          </div>

          {openSections.rmConsumption && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <DynamicTable
                sectionKey="rmConsumption"
                rows={rmRows}
                setRows={setRmRows}
                columns={[
                  {
                    field: "products",
                    header: "Product",
                    type: "select",
                    options: (productRows || [])
                      .map((p) => p.product)
                      .filter(Boolean),
                    width: 200,
                  },
                  {
                    field: "workingDays",
                    header: "Working Days",
                    type: "number",
                    width: 120,
                  },
                  {
                    field: "workingHoursPerDay",
                    header: "Working Hours/Day",
                    type: "number",
                    width: 150,
                  },
                  {
                    field: "outputPerHour",
                    header: "Output/Hour (KG)",
                    type: "number",
                    width: 120,
                  },
                  {
                    field: "operationPeriod",
                    header: "1st Year Period",
                    width: 150,
                  },
                ]}
                rowDefaults={{
                  products: "",
                  workingDays: "",
                  workingHoursPerDay: "",
                  outputPerHour: "",
                  operationPeriod: "",
                }}
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium">
                    RM Consumption Particulars
                  </h3>
                  <div className="flex space-x-2 no-print">
                    <button
                      onClick={() =>
                        showGuide(
                          "RM Consumption Particulars",
                          "List each type of raw material, link it to a finished product, and enter quantity and price per KG."
                        )
                      }
                      className="p-1 rounded-full bg-green-100 hover:bg-green-200"
                    >
                      <InformationCircleIcon className="h-4 w-4 text-green-600" />
                    </button>
                    <button
                      onClick={() =>
                        setRmPartRows([
                          ...rmPartRows,
                          {
                            slno: "",
                            particular: "",
                            product: "",
                            quantity: "",
                            price: "",
                          },
                        ])
                      }
                      className="p-1 rounded-full bg-green-100 hover:bg-green-200"
                    >
                      <PlusIcon className="h-4 w-4 text-green-600" />
                    </button>
                  </div>
                </div>

                <DynamicTable
                  sectionKey="rmConsumptionParticulars"
                  rows={rmPartRows}
                  setRows={setRmPartRows}
                  columns={[
                    { field: "slno", header: "Sl No", width: 60 },
                    { field: "particular", header: "Particular", width: 200 },
                    {
                      field: "product",
                      header: "Product",
                      type: "select",
                      options: (productRows || [])
                        .map((p) => p.product)
                        .filter(Boolean),
                      width: 150,
                    },
                    {
                      field: "quantity",
                      header: "Quantity",
                      type: "number",
                      width: 100,
                    },
                    {
                      field: "price",
                      header: "Price (₹/KG)",
                      type: "number",
                      width: 120,
                    },
                  ]}
                  rowDefaults={{
                    slno: "",
                    particular: "",
                    product: "",
                    quantity: "",
                    price: "",
                  }}
                />
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("RM Consumption")}
              </div>
            </div>
          )}
        </section>

        {/* 14) Cost Of Production */}
        <section id="costOfProduction">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-medium">Cost Of Production</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Cost Of Production",
                    getGuideContent("Cost Of Production")
                  )
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() =>
                  setCostProdRows([
                    ...costProdRows,
                    { particular: "", percentage: "" },
                  ])
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <PlusIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() => next("costOfProduction")}
                className="p-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("costOfProduction")}
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                {openSections.costOfProduction ? (
                  <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.costOfProduction && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="costOfProduction"
                rows={costProdRows}
                setRows={setCostProdRows}
                columns={[
                  { field: "particular", header: "Particular", width: 300 },
                  {
                    field: "percentage",
                    header: "Percentage %",
                    type: "number",
                    width: 120,
                  },
                ]}
                rowDefaults={{ particular: "", percentage: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Cost Of Production")}
              </div>
            </div>
          )}
        </section>

        {/* 15) Power Section */}
        <section id="powerSection">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-teal-600" />
              <h2 className="text-base font-medium">Power</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() => showGuide("Power", getGuideContent("Power"))}
                className="p-1 rounded-full bg-teal-100 hover:bg-teal-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-teal-600" />
              </button>
              <button
                onClick={() =>
                  setPowerRows([...powerRows, { particular: "", value: "" }])
                }
                className="p-1 rounded-full bg-teal-100 hover:bg-teal-200"
              >
                <PlusIcon className="h-4 w-4 text-teal-600" />
              </button>
              <button
                onClick={() => next("powerSection")}
                className="p-1 rounded-full bg-teal-600 hover:bg-teal-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("powerSection")}
                className="p-1 rounded-full bg-teal-100 hover:bg-teal-200"
              >
                {openSections.powerSection ? (
                  <ChevronUpIcon className="h-4 w-4 text-teal-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-teal-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.powerSection && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="powerSection"
                rows={powerRows}
                setRows={setPowerRows}
                columns={[
                  {
                    field: "particular",
                    header: "Particular",
                    type: "select",
                    options: powerOptions,
                    width: 250,
                  },
                  { field: "value", header: "Value", width: 150 },
                ]}
                rowDefaults={{ particular: "", value: "" }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("Power")}
              </div>
            </div>
          )}
        </section>

        {/* 16) Annual Sales Realisation */}
        <section id="annualSales">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-orange-600" />
              <h2 className="text-base font-medium">
                Annual Sales Realisation
              </h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Annual Sales Realisation",
                    getGuideContent("Annual Sales Realisation")
                  )
                }
                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-orange-600" />
              </button>
              <button
                onClick={() =>
                  setSalesRows([
                    ...salesRows,
                    { product: "", avgSalePrice: "", inventoryDays: "" },
                  ])
                }
                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200"
              >
                <PlusIcon className="h-4 w-4 text-orange-600" />
              </button>
              <button
                onClick={() => next("annualSales")}
                className="p-1 rounded-full bg-orange-600 hover:bg-orange-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("annualSales")}
                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200"
              >
                {openSections.annualSales ? (
                  <ChevronUpIcon className="h-4 w-4 text-orange-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-orange-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.annualSales && (
            <div className="bg-white p-3 rounded shadow mt-1">
              <DynamicTable
                sectionKey="annualSales"
                rows={salesRows}
                setRows={setSalesRows}
                columns={[
                  {
                    field: "product",
                    header: "Product",
                    type: "select",
                    options: (productRows || [])
                      .map((p) => p.product)
                      .filter(Boolean),
                    width: 200,
                  },
                  {
                    field: "avgSalePrice",
                    header: "Avg Sale Price / MT (Rs.)",
                    type: "number",
                    width: 200,
                  },
                  {
                    field: "inventoryDays",
                    header: "Finished Good Inventory Days",
                    type: "number",
                    width: 200,
                  },
                ]}
                rowDefaults={{
                  product: "",
                  avgSalePrice: "",
                  inventoryDays: "",
                }}
              />

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Annual Sales Realisation")}
              </div>
            </div>
          )}
        </section>

        {/* 17) Working Capital Requirement */}
        <section id="workingCapitalRequirement">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-base font-medium">
                Working Capital Requirement
              </h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Working Capital Requirement",
                    getGuideContent("Working Capital Requirement")
                  )
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() =>
                  setWcRows([...wcRows, { particular: "", days: "" }])
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <PlusIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("workingCapitalRequirement")}
                className="p-1 rounded-full bg-green-600 hover:bg-green-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("workingCapitalRequirement")}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                {openSections.workingCapitalRequirement ? (
                  <ChevronUpIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-green-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.workingCapitalRequirement && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <DynamicTable
                sectionKey="workingCapitalRequirement"
                rows={wcRows}
                setRows={setWcRows}
                columns={[
                  {
                    field: "particular",
                    header: "Particular",
                    type: "select",
                    options: [
                      "Raw Materials",
                      "Finished Goods & WIP",
                      "Sundry Debtors",
                      "Sundry Creditors",
                    ],
                    width: 300,
                  },
                  { field: "days", header: "Days", type: "number", width: 120 },
                ]}
                rowDefaults={{ particular: "", days: "" }}
              />

              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2 text-center">
                  Bank Contribution & Interest On CC
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700">
                      Bank Contribution (%)
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded text-center"
                      value={workingCapitalInputs.bankContribution}
                      onChange={(e) =>
                        setWorkingCapitalInputs({
                          ...workingCapitalInputs,
                          bankContribution: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">
                      Interest On CC (%)
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded text-center"
                      value={workingCapitalInputs.interestOnCC}
                      onChange={(e) =>
                        setWorkingCapitalInputs({
                          ...workingCapitalInputs,
                          interestOnCC: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Working Capital Requirement")}
              </div>
            </div>
          )}
        </section>

        {/* 18) Projected Profitability */}
        <section id="projectedProfitability">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="h-5 w-5 text-pink-600" />
              <h2 className="text-base font-medium">Projected Profitability</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Projected Profitability",
                    getGuideContent("Projected Profitability")
                  )
                }
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-pink-600" />
              </button>
              <button
                onClick={() => next("projectedProfitability")}
                className="p-1 rounded-full bg-pink-600 hover:bg-pink-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("projectedProfitability")}
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                {openSections.projectedProfitability ? (
                  <ChevronUpIcon className="h-4 w-4 text-pink-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-pink-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.projectedProfitability && (
            <div className="bg-white p-3 rounded shadow mt-1 grid grid-cols-1 md:grid-cols-5 gap-4 text-center">
              {Object.entries(projProfit).map(([f, v]) => (
                <div key={f}>
                  <label className="block text-sm text-gray-700">
                    {f
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())}
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    value={v}
                    onChange={(e) =>
                      setProjProfit({ ...projProfit, [f]: e.target.value })
                    }
                  />
                </div>
              ))}

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2 md:col-span-5">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Projected Profitability")}
              </div>
            </div>
          )}
        </section>

        {/* 19) Break-Even Point Analysis */}
        <section id="breakEven">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="h-5 w-5 text-yellow-600" />
              <h2 className="text-base font-medium">
                Break-Even Point Analysis
              </h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Break-Even Point Analysis",
                    getGuideContent("Break-Even Point Analysis")
                  )
                }
                className="p-1 rounded-full bg-yellow-100 hover:bg-yellow-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-yellow-600" />
              </button>
              <button
                onClick={() => next("breakEven")}
                className="p-1 rounded-full bg-yellow-600 hover:bg-yellow-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("breakEven")}
                className="p-1 rounded-full bg-yellow-100 hover:bg-yellow-200"
              >
                {openSections.breakEven ? (
                  <ChevronUpIcon className="h-4 w-4 text-yellow-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-yellow-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.breakEven && (
            <div className="bg-white p-3 rounded shadow mt-1 grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
              {Object.entries(breakEven).map(([f, v]) => (
                <div key={f}>
                  <label className="block text-sm text-gray-700">
                    {f.replace(/([A-Z])/g, " $1")}
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={v}
                    onChange={(e) =>
                      setBreakEven({ ...breakEven, [f]: e.target.value })
                    }
                  />
                </div>
              ))}

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2 md:col-span-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Break-Even Point Analysis")}
              </div>
            </div>
          )}
        </section>

        {/* 20) Balance Sheet Inputs */}
        <section id="balanceSheet">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-indigo-700" />
              <h2 className="text-base font-medium">Balance Sheet Inputs</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Balance Sheet Inputs",
                    getGuideContent("Balance Sheet Inputs")
                  )
                }
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
              </button>
              <button
                onClick={() => next("balanceSheet")}
                className="p-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("balanceSheet")}
                className="p-1 rounded-full bg-indigo-100 hover:bg-indigo-200"
              >
                {openSections.balanceSheet ? (
                  <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.balanceSheet && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                <div>
                  <label className="block text-sm text-gray-700">
                    Deposits (B)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={balanceSheet.depositsB}
                    onChange={(e) =>
                      setBalanceSheet({
                        ...balanceSheet,
                        depositsB: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">
                    Loan & Advances (%)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={balanceSheet.loanAndAdvances}
                    onChange={(e) =>
                      setBalanceSheet({
                        ...balanceSheet,
                        loanAndAdvances: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Other Current Assets
                </label>
                <div className="grid grid-cols-1 md:grid-cols-8 gap-4 text-center">
                  {projectYears.map((yr, i) => (
                    <div key={i}>
                      <label className="block text-sm text-gray-600">
                        {yr}
                      </label>
                      <input
                        type="number"
                        className="w-full p-1 border rounded text-center"
                        value={otherCurrentAssetsByYear[i] || ""}
                        onChange={(e) => {
                          const a = [...otherCurrentAssetsByYear];
                          a[i] = e.target.value;
                          setOtherCurrentAssetsByYear(a);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Balance Sheet Inputs")}
              </div>
            </div>
          )}
        </section>

        {/* 21) Projected Cash Flow & Promoter’s Drawings */}
        <section id="projectedCashFlow">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <CurrencyRupeeIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-base font-medium">
                Projected Cash Flow & Promoter’s Drawings
              </h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Projected Cash Flow & Promoter’s Drawings",
                    getGuideContent("Projected Cash Flow & Promoter’s Drawings")
                  )
                }
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={() => next("projectedCashFlow")}
                className="p-1 rounded-full bg-green-600 hover:bg-green-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("projectedCashFlow")}
                className="p-1 rounded-full bg-green-100 hover:bg-green-200"
              >
                {openSections.projectedCashFlow ? (
                  <ChevronUpIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-green-600" />
                )}
              </button>
            </div>
          </div>
          {openSections.projectedCashFlow && (
            <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                {projectYears.slice(1).map((yr, i) => (
                  <div key={i}>
                    <label className="block text-sm text-gray-700">
                      Fixed Assets {yr}
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded text-center"
                      value={projCashFlow[i] || ""}
                      onChange={(e) => {
                        const a = [...projCashFlow];
                        a[i] = e.target.value;
                        setProjCashFlow(a);
                      }}
                    />
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2">
                  Promoter’s Drawings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
                  {projectYears.map((yr, i) => (
                    <div key={i} className="text-center">
                      <label className="block text-sm text-gray-700">
                        {yr}
                      </label>
                      <input
                        type="number"
                        className="w-full p-2 border rounded text-center"
                        value={promoterDrawings[i] || ""}
                        onChange={(e) => {
                          const a = [...promoterDrawings];
                          a[i] = e.target.value;
                          setPromoterDrawings(a);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Projected Cash Flow & Promoter’s Drawings")}
              </div>
            </div>
          )}
        </section>

        {/* 22) Sensitivity Analysis */}
        <section id="sensitivityAnalysis">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-5 w-5 text-pink-600" />
              <h2 className="text-base font-medium">Sensitivity Analysis</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide(
                    "Sensitivity Analysis",
                    getGuideContent("Sensitivity Analysis")
                  )
                }
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-pink-600" />
              </button>
              <button
                onClick={() => next("sensitivityAnalysis")}
                className="p-1 rounded-full bg-pink-600 hover:bg-pink-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("sensitivityAnalysis")}
                className="p-1 rounded-full bg-pink-100 hover:bg-pink-200"
              >
                {openSections.sensitivityAnalysis ? (
                  <ChevronUpIcon className="h-4 w-4 text-pink-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-pink-600" />
                )}
              </button>
            </div>
          </div>

          {openSections.sensitivityAnalysis && (
            <>
              <div className="bg-white p-3 rounded shadow mt-1 grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                <div>
                  <label className="block text-sm text-gray-700">
                    Scenario 1 (% ↓ Price)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={sens1}
                    onChange={(e) => setSens1(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">
                    Scenario 2 (% ↑ Cost)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded text-center"
                    value={sens2}
                    onChange={(e) => setSens2(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong>{" "}
                {getGuideContent("Sensitivity Analysis")}
              </div>
            </>
          )}
        </section>

        {/* 23) Term Loan Repayment / EMI Schedule */}
        <section id="termLoanRepayment">
          <div className="flex items-center justify-between bg-white p-3 shadow rounded">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-yellow-600" />
              <h2 className="text-base font-medium">EMI Schedule</h2>
            </div>
            <div className="flex items-center space-x-2 no-print">
              <button
                onClick={() =>
                  showGuide("EMI Schedule", getGuideContent("EMI Schedule"))
                }
                className="p-1 rounded-full bg-yellow-100 hover:bg-yellow-200"
              >
                <InformationCircleIcon className="h-4 w-4 text-yellow-600" />
              </button>
              <button
                onClick={() => next("termLoanRepayment")}
                className="p-1 rounded-full bg-yellow-600 hover:bg-yellow-700"
              >
                <ArrowRightIcon className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => toggle("termLoanRepayment")}
                className="p-1 rounded-full bg-yellow-100 hover:bg-yellow-200"
              >
                {openSections.termLoanRepayment ? (
                  <ChevronUpIcon className="h-4 w-4 text-yellow-600" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-yellow-600" />
                )}
              </button>
            </div>
          </div>

          {openSections.termLoanRepayment && (
            <>
              <div className="bg-white p-3 rounded shadow mt-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-center">
                  {[
                    "interestRate",
                    "loanPeriod",
                    "moratorium",
                    "startMonth",
                    "pStartMonth",
                  ].map((f) => (
                    <div key={f}>
                      <label className="block text-sm text-gray-700">
                        {
                          {
                            interestRate: "Interest Rate (%)",
                            loanPeriod: "Loan Period (Months)",
                            moratorium: "Moratorium (Months)",
                            startMonth: "Commercial Production (mm-yyyy)",
                            pStartMonth: "Project Start Month (mm-yyyy)",
                          }[f]
                        }
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-center"
                        value={termLoan[f]}
                        onChange={(e) =>
                          setTermLoan({ ...termLoan, [f]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-center">
                    Disbursement Schedule
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    {termLoan.disbursements.map((d, i) => (
                      <div key={i} className="relative">
                        <input
                          type="text"
                          placeholder={`Disbursement ${i + 1}`}
                          className="w-full p-2 border rounded text-center"
                          value={d}
                          onChange={(e) => updateDisb(i, e.target.value)}
                        />
                        <button
                          onClick={() => removeDisb(i)}
                          className="absolute top-1 right-1 p-1 bg-red-100 rounded hover:bg-red-200 no-print"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addDisb}
                      className="p-2 rounded-full bg-green-100 hover:bg-green-200 no-print"
                    >
                      <PlusIcon className="h-5 w-5 text-green-600" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-3 hidden print:block text-xs text-gray-600 border-t border-dashed pt-2">
                <strong>Guide:</strong> {getGuideContent("EMI Schedule")}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
